import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  canHangUpPhoneLeg,
  checkOutboundNumber,
  type Huddle,
  type PhoneLeg,
} from '@avslack/shared';
import { dialPhone, hangUpPhoneLeg, transferCall } from '../lib/api';
import { useActiveHuddles } from '../data/huddles';
import { openHuddleWindow } from '../lib/huddle-window';
import { rememberLine, useCallableLines } from '../data/phone';
import { LinePicker } from './LinePicker';
import { usePhoneContacts } from '../data/phone';
import { setPendingDial } from '../lib/pending-dial';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { TaskLink } from './TaskLink';
import { useDismiss } from '../lib/dismiss';

/**
 * A hivas TELEFONOS fele - kulon sav a resztvevok felett.
 *
 * Nem resztvevo-csempe: a hivonak nincs felhasznaloja, avatarja, kamerája, es
 * nem is szabad annak latszania. Amit a kollegaknak tudniuk kell: KI hiv
 * (szam, vonal), hol tart a hivas (csorog, hivasban, letette), beszel-e eppen,
 * es mit tehetnek vele (letenni, atadni).
 */
export function PhoneLegs({
  huddle,
  meId,
  speakingIds,
}: {
  huddle: Huddle;
  meId: string;
  speakingIds: string[];
}) {
  const amJoined = huddle.participants.some((p) => p.user.id === meId && p.state === 'joined');

  /*
    Ami latszik: az elo telefonos fel - vagy ha nincs, az utolso lezart, hogy
    lassuk, mi lett vele. Egy hivasban egymas utan tobb kimeno hivas is lehet;
    a regieknek a csatorna naplojaban a helyuk, nem itt.
  */
  const live = huddle.phoneLegs.filter((leg) => leg.state !== 'ended');
  const last = huddle.phoneLegs.at(-1);
  const visible = live.length > 0 ? live : last ? [last] : [];

  if (visible.length === 0) return null;

  return (
    <ul className="space-y-2">
      {visible.map((leg) => (
        <PhoneLegRow
          key={leg.id}
          huddle={huddle}
          leg={leg}
          meId={meId}
          canAct={amJoined}
          isSpeaking={speakingIds.includes(leg.identity)}
        />
      ))}
    </ul>
  );
}

/**
 * Latszik-e a tarcsazo: csak bent ulve, kimeno-joggal, es amig nincs elo
 * telefonos fel. A tarcsazo a hivas fejleceben all (lasd HuddleRoom).
 */
export function useShowDialer(huddle: Huddle | null, meId: string): boolean {
  const { users } = useUsers();
  if (!huddle) return false;
  const amJoined = huddle.participants.some((p) => p.user.id === meId && p.state === 'joined');
  const canCallOut = users.find((user) => user.id === meId)?.canCallOut ?? false;
  return canCallOut && amJoined && huddle.phoneLegs.every((leg) => leg.state === 'ended');
}

/** Elo szamlalo (m:ss) egy idoponttol - a hivas savjan es a hivasnaploban. */
export function useElapsed(since: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  if (!since) return '';
  const seconds = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function PhoneLegRow({
  huddle,
  leg,
  meId,
  canAct,
  isSpeaking,
}: {
  huddle: Huddle;
  leg: PhoneLeg;
  meId: string;
  canAct: boolean;
  isSpeaking: boolean;
}) {
  const { nameOf } = useUsers();
  const phoneContacts = usePhoneContacts();
  const [busy, setBusy] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const transferButtonRef = useRef<HTMLButtonElement | null>(null);
  const elapsed = useElapsed(leg.state === 'active' ? leg.answeredAt : null);

  const outbound = leg.direction === 'outbound';
  const status =
    leg.state === 'ringing'
      ? outbound
        ? 'Kicsöng…'
        : 'Csörög…'
      : leg.state === 'active'
        ? `Hívásban · ${elapsed}`
        : outbound
          ? leg.answeredAt
            ? 'Bontva'
            : 'Nem vették fel'
          : 'A hívó letette';

  const hangUp = async () => {
    setBusy(true);
    try {
      await hangUpPhoneLeg(huddle.id, leg.id);
    } finally {
      setBusy(false);
    }
  };

  const ended = leg.state === 'ended';
  /*
    A telefont csak a hivas gazdaja bontja (lasd canHangUpPhoneLeg). A tobbiek
    a hivasbol a "Kilépés" gombbal mennek ki - a telefonos fel ettol marad.
  */
  const canHangUp = canHangUpPhoneLeg(
    leg,
    huddle.participants.filter((p) => p.state === 'joined').map((p) => p.user.id),
    meId,
  );

  return (
    <li
      className={`relative flex flex-wrap items-center gap-3 rounded-card border px-3.5 py-2.5 ${
        ended
          ? 'border-border-soft bg-surface-sunken opacity-70'
          : isSpeaking
            ? 'border-accent bg-accent-soft'
            : 'border-border bg-surface-raised'
      }`}
    >
      <span
        aria-hidden
        className={`grid size-10 shrink-0 place-items-center rounded-full ${
          ended ? 'bg-surface-hover text-ink-faint' : 'bg-accent text-accent-ink'
        } ${leg.state === 'ringing' ? 'animate-pulse' : ''}`}
      >
        <Icon name="telefon" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-semibold tabular-nums">
          {phoneContacts.display(leg.callerNumber)}
        </p>
        <p className="truncate text-xs text-ink-muted">
          {outbound ? 'Kimenő hívás · ' : leg.lineLabel ? `${leg.lineLabel} · ` : ''}
          {status}
          {leg.transferTo && ` · átadás: ${nameOf(leg.transferTo)}`}
        </p>
        {leg.task && <TaskLink task={leg.task} />}
      </div>

      {canAct && !ended && (
        <div className="flex items-center gap-1.5">
          {leg.state === 'active' && (
            <button
              ref={transferButtonRef}
              type="button"
              aria-expanded={transferOpen}
              onClick={() => setTransferOpen((open) => !open)}
              className="flex items-center gap-1.5 rounded-control border border-border px-2.5 py-1.5 text-xs text-ink-muted transition-colors hover:text-ink"
            >
              <Icon name="atadas" size="sm" /> Átadás
            </button>
          )}
          {/*
            NEM a kilepes gombja: mas felirat, mas ikon. Eles hiba volt, hogy a
            kilepni akaro kolléga ezt nyomta meg, es a hivott felet bontotta.
          */}
          {canHangUp && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void hangUp()}
              title="A telefonos fél bontása – a hívás a kollégákkal folytatódik"
              className="flex items-center gap-1.5 rounded-control bg-[var(--danger)] px-2.5 py-1.5 text-xs font-semibold text-white transition-transform hover:brightness-110 active:scale-95 disabled:opacity-50"
            >
              <Icon name="bezaras" size="sm" /> Telefon bontása
            </button>
          )}
        </div>
      )}

      {transferOpen && (
        <TransferPanel
          huddle={huddle}
          meId={meId}
          triggerRef={transferButtonRef}
          onClose={() => setTransferOpen(false)}
        />
      )}
    </li>
  );
}

/**
 * TARCSAZO: kimeno hivas a huddle-bol.
 *
 * Csak annal latszik, akinek az admin megadta a jogot. A szamszabalyt itt csak
 * elore jelezzuk (hogy ne kelljen a szerverig menni egy elgepelesert); a
 * dontes a szervere.
 */
export function PhoneDialer({ huddleId, channelId }: { huddleId: string; channelId: string }) {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: lines } = useCallableLines(open);
  const [lineId, setLineId] = useState<string | null>(null);

  const trigger = (
    <button
      type="button"
      aria-expanded={open}
      onClick={() => setOpen((value) => !value)}
      title="Telefonszám hívása"
      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-control border border-border px-2.5 py-1 text-xs text-ink-muted transition-colors hover:text-ink"
    >
      <Icon name="telefon" size="sm" /> <span className="hidden sm:inline">Telefonszám hívása</span>
    </button>
  );

  if (!open) return trigger;

  const call = async () => {
    const check = checkOutboundNumber(number);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await dialPhone(huddleId, check.number, lineId ?? undefined);
      if (lineId) rememberLine(lineId);
      setNumber('');
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'A hívás nem indult el.');
    } finally {
      setBusy(false);
    }
  };

  /*
    A fejlecben csak a gomb fer el; a szammezo legordulo panelben nyilik a
    fejlec ala (a fejlec a pozicionalo szulo).
  */
  return (
    <>
      {trigger}
      <form
        className="absolute left-3 right-3 top-full z-20 mt-1 flex flex-wrap items-center gap-2 rounded-card border border-border bg-surface-raised px-3.5 py-2.5 shadow-modal sm:right-auto sm:w-[30rem]"
        onSubmit={(event) => {
          event.preventDefault();
          void call();
        }}
      >
        <span aria-hidden className="text-ink-muted">
          <Icon name="telefon" />
        </span>
        <input
          aria-label="Hívandó szám"
          autoFocus
          inputMode="tel"
          value={number}
          onChange={(event) => setNumber(event.target.value)}
          placeholder="06 30 123 4567"
          className="min-w-0 flex-1 rounded-control border border-border bg-surface px-3 py-1.5 text-sm tabular-nums"
        />
        <LinePicker lines={lines ?? []} value={lineId} onChange={setLineId} channelId={channelId} />
        <button
          type="submit"
          disabled={busy || number.trim().length < 3}
          className="rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink disabled:opacity-50"
        >
          Hívás
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="text-xs text-ink-muted hover:text-ink"
        >
          Mégse
        </button>
        {error && (
          <p role="alert" className="w-full text-xs text-danger">
            {error}
          </p>
        )}
      </form>
    </>
  );
}

/**
 * Visszahivas egy szamra a csatornabol.
 *
 * A hivas MINDIG a kulon hivas-ablakban fut. Ha mar bent ulunk a csatorna
 * hivasaban, azonnal tarcsazunk; ha nem, felirjuk a szamot, megnyitjuk az
 * ablakot, es az tarcsaz, amint belepett (lasd pending-dial.ts). A fo ablak
 * maga nem lep be: az "Ablak" gombbal atvett hivas kulonben megszakadna.
 */
export function useDialFromChannel(channelId: string, meId: string) {
  const { data: huddles } = useActiveHuddles();
  const [error, setError] = useState<string | null>(null);

  const live = huddles?.find((item) => item.channelId === channelId) ?? null;
  const inside =
    live?.participants.some((p) => p.user.id === meId && p.state === 'joined') ?? false;
  // Masik csatorna hivasaban ulunk: onnan nem tarcsazunk ide.
  const elsewhere =
    huddles?.some(
      (item) =>
        item.channelId !== channelId &&
        item.participants.some((p) => p.user.id === meId && p.state === 'joined'),
    ) ?? false;

  const dial = (number: string, lineId?: string) => {
    setError(null);
    if (lineId) rememberLine(lineId);
    if (live && inside) {
      dialPhone(live.id, number, lineId).catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'A hívás nem indult el.'),
      );
      return;
    }
    setPendingDial(channelId, number, lineId);
    // SZINKRON a kattintasban: kulonben a bongeszo blokkolja a felugro ablakot.
    void openHuddleWindow(channelId).then((opened) => {
      if (!opened)
        setError('A böngésző blokkolta a hívás ablakát – engedélyezd a felugró ablakot.');
    });
  };

  return { dial, error, busy: elsewhere };
}

/**
 * Atadas: kinek adjuk at a hivast.
 *
 * A kivalasztott kolléga behivast kap; amikor belep, mi kilepunk - a hivas
 * kozben egy pillanatra sem marad gazda nelkul. Ha nemet mond, mi bent
 * maradunk, es a hivo semmit nem vesz eszre.
 */
function TransferPanel({
  huddle,
  meId,
  triggerRef,
  onClose,
}: {
  huddle: Huddle;
  meId: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}) {
  const { users, nameOf } = useUsers();
  const panelRef = useRef<HTMLDivElement | null>(null);
  useDismiss(panelRef, onClose, { ignore: [triggerRef], returnFocus: triggerRef });
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const inside = new Set(
    huddle.participants.filter((p) => p.state === 'joined').map((p) => p.user.id),
  );
  const candidates = users
    .filter((user) => !user.isBot && user.id !== meId && !inside.has(user.id))
    .filter((user) => nameOf(user).toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 8);

  const choose = async (userId: string) => {
    setError(null);
    try {
      await transferCall(huddle.id, userId);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Az átadás nem sikerült.');
    }
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-3 top-full z-20 mt-1 w-72 rounded-card border border-border bg-surface-raised p-3 shadow-modal"
    >
      <input
        aria-label="Kolléga keresése az átadáshoz"
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Kinek adjuk át?"
        className="w-full rounded-control border border-border bg-surface px-3 py-2 text-sm"
      />
      {candidates.length === 0 ? (
        <p className="mt-2 text-xs text-ink-faint">
          {query.trim() ? 'Nincs ilyen nevű kolléga.' : 'Nincs elérhető kolléga.'}
        </p>
      ) : (
        <ul className="mt-2 max-h-72 space-y-0.5 overflow-y-auto">
          {candidates.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                onClick={() => void choose(user.id)}
                className="flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover"
              >
                <Avatar
                  userId={user.id}
                  name={user.displayName}
                  avatarUrl={user.avatarUrl}
                  size="sm"
                />
                <span className="min-w-0 flex-1 truncate">{nameOf(user)}</span>
                <span className="text-xs text-ink-faint">Átadás</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={onClose}
        className="mt-2 text-2xs text-ink-muted underline hover:text-ink"
      >
        Bezárás
      </button>
    </div>
  );
}
